import { Accept, useDropzone } from 'react-dropzone';
import UploadCloud2FillIcon from 'remixicon-react/UploadCloud2FillIcon';
import { Box, CircularProgress, Link as MuiLink, SxProps, Theme, Typography } from '@mui/material';
import { styled } from '@mui/material/styles';

type DropzoneProps = {
  accept?: Accept;
  disabled?: boolean;
  dropzonePlaceholder?: string;
  loadingFiles?: boolean;
  maxFiles?: number;
  sx?: SxProps<Theme>;
  multiple?: boolean;
  onDrop: (files: File[]) => void;
};

function Dropzone({
  accept,
  disabled,
  dropzonePlaceholder,
  loadingFiles,
  maxFiles,
  sx,
  multiple = true,
  onDrop,
}: DropzoneProps) {
  const { getRootProps, getInputProps } = useDropzone({
    onDrop,
    ...(maxFiles && { maxFiles }),
    disabled: disabled,
    multiple,
    ...(accept && { accept }),
  });

  return (
    <DropzoneContainer
      {...getRootProps({ className: 'dropzone' })}
      sx={(theme) => ({
        ...(typeof sx === 'function' ? (sx as Function)?.(theme) : sx),
        opacity: disabled ? 0.6 : 1,
      })}
    >
      <input className="input-zone" {...getInputProps()} disabled={disabled} />
      <Box display="flex" alignItems="center" justifyContent="center">
        {loadingFiles ? (
          <>
            <CircularProgress
              sx={(theme) => ({ color: theme.palette.custom.black, mx: 1 })}
              size={20}
            />{' '}
            Attaching files...
          </>
        ) : (
          <>
            <UploadCloud2FillIcon size={20} />
            <Typography ml={0.5} sx={{ opacity: 1 }}>
              {dropzonePlaceholder || 'Drop files here to attach'} or <Link>browse</Link>
            </Typography>
          </>
        )}
      </Box>
    </DropzoneContainer>
  );
}

const DropzoneContainer = styled(Box)(({ theme }) => ({
  textAlign: 'center',
  cursor: 'pointer',
  border: `1px solid ${theme.palette.custom.divider}`,
  padding: '16px 0',
  borderRadius: 8,
  backgroundColor: theme.palette.custom.alabaster,
}));

const Link = styled(MuiLink, { label: 'Link' })(({ theme }) => ({
  textDecoration: 'none',
  color: theme.palette.custom.link,
  '&:hover': {
    textDecoration: 'underline',
  },
}));

export default Dropzone;
